import {
  ClickAwayListener,
  InputAdornment,
  Popper,
  TextField,
} from "@mui/material";
import { Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";

const INITIAL_LAT = -6.175;
const INITIAL_LNG = 106.8266;
const INITIAL_ZOOM = 15;

const LocationAssign = ({ formik, geoAddress }) => {
  const map = useMap("google-map");
  const placesLibrary = useMapsLibrary("places");
  const geocodingLibrary = useMapsLibrary("geocoding");

  const formikRef = useRef(formik);
  formikRef.current = formik;

  const [placesService, setPlacesService] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const isFirstGeoAddressRun = useRef(true);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [anchorElSearch, setAnchorElSearch] = useState(null);
  const [latLng, setLatLng] = useState(null);
  const [cameraProps, setCameraProps] = useState({
    center: { lat: INITIAL_LAT, lng: INITIAL_LNG },
    zoom: INITIAL_ZOOM,
  });

  const getAddressFromCoordinates = useCallback(
    async (lat, lng) => {
      if (!geocoder) return;

      try {
        const response = await geocoder.geocode({ location: { lat, lng } });
        if (response.results && response.results.length > 0) {
          const address = response.results[0].formatted_address;
          formikRef.current.setFieldValue("address", address);
        }
      } catch (error) {
        console.error("Error fetching address:", error);
      }
    },
    [geocoder],
  );

  const handleMapClick = useCallback(
    (mapMouseEvent) => {
      const clickLatLng = mapMouseEvent.detail.latLng;
      if (!clickLatLng) return;

      setSearch("");
      setLatLng(clickLatLng);
      formikRef.current.setValues({
        ...formikRef.current.values,
        latitude: clickLatLng.lat,
        longitude: clickLatLng.lng,
      });
      getAddressFromCoordinates(clickLatLng.lat, clickLatLng.lng);
    },
    [getAddressFromCoordinates],
  );

  const setMarker = useCallback(
    (mark) => {
      placesService.getDetails({ placeId: mark.place_id }, (res) => {
        const lat = res.geometry.location.lat();
        const lng = res.geometry.location.lng();

        formikRef.current.setValues({
          ...formikRef.current.values,
          latitude: lat,
          longitude: lng,
        });
        setLatLng(res.geometry.location);
        setCameraProps({
          center: { lat, lng },
          zoom: INITIAL_ZOOM,
        });

        getAddressFromCoordinates(lat, lng);
      });
    },
    [placesService, getAddressFromCoordinates],
  );

  const handleCameraChange = useCallback((ev) => {
    setCameraProps(ev.detail);
  }, []);

  useEffect(() => {
    if (formik.values.latitude && formik.values.longitude) {
      const lat = parseFloat(formik.values.latitude);
      const lng = parseFloat(formik.values.longitude);
      setLatLng({ lat, lng });
      setCameraProps({
        center: { lat, lng },
        zoom: INITIAL_ZOOM,
      });
    }
  }, [formik.values.latitude, formik.values.longitude]);

  useEffect(() => {
    if (!placesLibrary || !map) return;
    setAutocompleteService(new placesLibrary.AutocompleteService(map));
    setPlacesService(new placesLibrary.PlacesService(map));
  }, [placesLibrary, map]);

  useEffect(() => {
    if (!geocodingLibrary || !map) return;
    setGeocoder(new geocodingLibrary.Geocoder());
  }, [geocodingLibrary, map]);

  useEffect(() => {
    if (!geocoder || !geoAddress) return;

    if (isFirstGeoAddressRun.current) {
      isFirstGeoAddressRun.current = false;
      if (
        formikRef.current.values.latitude !== 0 ||
        formikRef.current.values.longitude !== 0
      )
        return;
    }

    const parts = geoAddress.split(",").filter(Boolean).length;
    const zoom = parts >= 4 ? 16 : parts >= 3 ? 14 : parts >= 2 ? 12 : 10;

    geocoder
      .geocode({ address: geoAddress })
      .then((response) => {
        if (response.results && response.results.length > 0) {
          const location = response.results[0].geometry.location;
          setCameraProps({
            center: { lat: location.lat(), lng: location.lng() },
            zoom,
          });
        }
      })
      .catch(() => {});
  }, [geocoder, geoAddress]);

  useEffect(() => {
    if (!autocompleteService || search.length === 0) {
      setResults([]);
      return;
    }

    autocompleteService.getQueryPredictions({ input: search }, (res) => {
      setResults(res);
    });
  }, [autocompleteService, search]);

  return (
    <div className="mb-4">
      <div className="w-full">
        <TextField
          autoComplete="off"
          fullWidth
          placeholder="Cari lokasi"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiSearch size={18} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            style: {
              fontSize: 13,
              backgroundColor: "#fff",
            },
          }}
          sx={{
            backgroundColor: "#fff",
            "& ::placeholder": { fontSize: "14px" },
            fontSize: "14px",
            height: "40px",
            borderRadius: "32px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "32px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#94c679",
              },
            },
            "& .MuiInputBase-input": {
              color: "#111827",
              fontSize: { xs: "14px", lg: "16px" },
              fontFamily: '"Montserrat", sans-serif',
              "&::placeholder": {
                color: "#CBCBCB",
                opacity: 1,
                fontFamily: '"Montserrat", sans-serif',
              },
            },
            marginBottom: 1,
            fontFamily: '"Montserrat", sans-serif',
          }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value.length > 0) setAnchorElSearch(e.currentTarget);
          }}
          onClick={(e) => {
            if (search.length > 0) setAnchorElSearch(e.currentTarget);
          }}
        />
        <Popper
          disablePortal
          open={Boolean(anchorElSearch)}
          anchorEl={anchorElSearch}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          style={{ width: anchorElSearch?.clientWidth, zIndex: 1500 }}
        >
          <ClickAwayListener onClickAway={() => setAnchorElSearch(null)}>
            <div className="w-full bg-white rounded-b-md shadow-md max-h-[30vh] overflow-y-auto">
              {results && results.length > 0 ? (
                results.map((res) => {
                  return (
                    <div
                      key={res.place_id}
                      className="p-2 border-b-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setSearch(res.structured_formatting.main_text);
                        setAnchorElSearch(null);
                        setMarker(res);
                      }}
                    >
                      <p className="font-bold mb-2">
                        {res.structured_formatting.main_text}
                      </p>
                      <p className="">
                        {res.structured_formatting.secondary_text}
                      </p>
                    </div>
                  );
                })
              ) : (
                <div className="p-2 text-center w-full text-gray-300">
                  Lokasi tidak ditemukan
                </div>
              )}
            </div>
          </ClickAwayListener>
        </Popper>
      </div>
      <div className="flex justify-center items-center min-h-[30vh]">
        <div className="w-full lg:w-[45vw] h-[100vw] md:h-[70vw] lg:h-[50vw] mb-1 flex justify-center items-center">
          <Map
            id="google-map"
            center={latLng}
            defaultZoom={INITIAL_ZOOM}
            onCameraChanged={handleCameraChange}
            onClick={handleMapClick}
            {...cameraProps}
            className="w-full h-full rounded-xl overflow-hidden"
            style={{
              background: "#fffddd",
            }}
          >
            <Marker position={latLng} />
          </Map>
        </div>
      </div>
      {formik.values.latitude === 0 && formik.values.longitude === 0 ? (
        <p className="italic text-sm text-gray-500">
          *Silahkan pilih titik lokasi pengiriman anda
        </p>
      ) : null}
    </div>
  );
};

export default LocationAssign;
